import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import api, { formatApiError, eur } from "@/lib/api";
import { Btn, Field, inputCls } from "@/components/common";
import PassportScanner from "@/components/PassportScanner";
import { ExtraGuests } from "@/components/ExtraGuests";
import { GroupRooms } from "@/components/GroupRooms";
import { EMPTY_BOOKING, isoAdd, resolveName, sbOptions } from "@/lib/bookingUtils";
import CardPayments from "@/components/CardPayments";
import { useSpeedboat, sbSlots } from "@/lib/speedboat";

const ageOn = (bd, ref) => {
  try {
    const b = new Date(bd), r = new Date(ref);
    if (isNaN(b) || isNaN(r)) return null;
    let y = r.getFullYear() - b.getFullYear();
    const m = r.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && r.getDate() < b.getDate())) y--;
    return y;
  } catch { return null; }
};

export default function BookingForm({ properties, origini, agencies, isAdmin, user, initial, groupEdit, onSaved, onCancel }) {
  const sb = useSpeedboat();

  const isGroupEdit = !!groupEdit;
  const primaryId = groupEdit?.rooms?.[0]?.id;
  const [form, setForm] = useState(() => {
    if (groupEdit) {
      const primary = groupEdit.rooms[0] || {};
      return {
        ...EMPTY_BOOKING, ...primary,
        group_rooms: groupEdit.rooms.slice(1).map((b) => ({
          _uid: Math.random().toString(36).slice(2), id: b.id,
          property_id: b.property_id, adults: b.adults ?? 1, children: b.children ?? 0,
          age_adults: b.age_adults ?? b.adults ?? 1, age_child_5_13: b.age_child_5_13 ?? 0,
          age_child_2_4: b.age_child_2_4 ?? 0, age_infant: b.age_infant ?? 0,
          total_amount: b.total_amount ?? 0, acconto: b.acconto ?? 0, saldo: b.saldo ?? 0,
          acconto_method: b.acconto_method || "conto_corrente", saldo_method: b.saldo_method || "conto_corrente",
          payment_status: b.payment_status || "none", no_commission: !!b.no_commission,
          extra_guests: b.extra_guests || [],
        })),
      };
    }
    return initial ? { ...EMPTY_BOOKING, ...initial } : {
      ...EMPTY_BOOKING, origine: isAdmin ? "Diretto" : user?.agency_name || "Diretto",
    };
  });
  const editId = initial?.id;
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [renewDays, setRenewDays] = useState("7");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInstallments = (checked) => setForm((f) => {
    if (!checked) return { ...f, installments_enabled: false };
    const ex = f.installments || [];
    const rows = Array.from({ length: 4 }, (_, i) => ex[i] || { due_date: "", method: "conto_corrente", paid: false });
    return { ...f, installments_enabled: true, installments: rows };
  });
  const setInstallment = (i, k, v) => setForm((f) => {
    const rows = [...(f.installments || [])];
    rows[i] = { ...rows[i], [k]: v };
    return { ...f, installments: rows };
  });
  const instAcconto = Number((form.installments || [])[0]?.amount || 0);
  const instRemainder = Math.max(Number(form.total_amount || 0) - instAcconto, 0);
  const instAmount = (i) => {
    if (i === 0) return instAcconto;
    const base = Math.round((instRemainder / 3) * 100) / 100;
    return i < 3 ? base : Math.round((instRemainder - base * 2) * 100) / 100;
  };
  const instPaid = [0, 1, 2, 3].reduce((a, i) => a + ((form.installments || [])[i]?.paid ? instAmount(i) : 0), 0);
  const renewOption = () => {
    const x = parseInt(renewDays, 10);
    if (!x) { toast.error("Inserisci i giorni di rinnovo"); return; }
    const nd = isoAdd(null, x);
    set("option_expiry_date", nd);
    toast.success(`Nuova scadenza opzione: ${nd}`);
  };

  const addSegment = () => setForm((f) => {
    const segs = f.segments || [];
    const row = segs.length === 0
      ? { property_id: f.property_id, checkin: f.checkin, checkout: f.checkout }
      : { property_id: "", checkin: "", checkout: "" };
    return { ...f, segments: [...segs, row] };
  });
  const setSegment = (i, k, v) => setForm((f) => {
    const segs = [...(f.segments || [])];
    segs[i] = { ...segs[i], [k]: v };
    return { ...f, segments: segs };
  });
  const delSegment = (i) => setForm((f) => ({ ...f, segments: (f.segments || []).filter((_, idx) => idx !== i) }));

  useEffect(() => {
    const { property_id, checkin, checkout, status } = form;
    if (!property_id || !checkin || !checkout || status === "cancellata") { setConflict(null); return; }
    let active = true;
    api.get("/bookings/availability", { params: { property_id, checkin, checkout, exclude: editId || primaryId || "" } })
      .then((r) => { if (active) setConflict(r.data.available ? null : r.data.conflicts[0]); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property_id, form.checkin, form.checkout, form.status]);

  // new booking: default acconto scadenza = oggi + 7 giorni
  useEffect(() => {
    if (!initial && !groupEdit) set("acconto_due_date", isoAdd(null, 7));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTotalChange = (v) => {
    const t = Number(v || 0);
    setForm((f) => ({
      ...f, total_amount: v,
      acconto: Math.round(t * 0.3 * 100) / 100,
      saldo: Math.round(t * 0.7 * 100) / 100,
    }));
  };

  const onCheckinChange = (v) => {
    setForm((f) => {
      const needCheckout = !f.checkout || f.checkout <= v;
      return {
        ...f, checkin: v,
        checkout: v && needCheckout ? isoAdd(v, 1) : f.checkout,
        saldo_due_date: v ? isoAdd(v, -21) : f.saldo_due_date,
      };
    });
  };

  const onScan = (data, dataUrl) => {
    const { name, surname } = resolveName(data);
    setForm((f) => ({
      ...f,
      guest_name: name || f.guest_name,
      guest_surname: surname || f.guest_surname,
      passport_number: data.passport_number || f.passport_number,
      nationality: data.nationality || f.nationality,
      birth_date: data.birth_date || f.birth_date,
      sex: data.sex || f.sex,
      expiry_date: data.expiry_date || f.expiry_date,
      country: data.country || f.country,
      passport_image: dataUrl || f.passport_image,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const extraRooms = (form.group_rooms || []).filter((r) => r.property_id);
    if (isGroupEdit) {
      const mkRoom = (r) => ({
        ...(r.id ? { id: r.id } : {}),
        property_id: r.property_id, adults: Number(r.adults) || 1, children: Number(r.children) || 0,
        age_adults: Number(r.age_adults) || Number(r.adults) || 0, age_child_5_13: Number(r.age_child_5_13) || 0,
        age_child_2_4: Number(r.age_child_2_4) || 0, age_infant: Number(r.age_infant) || 0,
        total_amount: Number(r.total_amount) || 0, acconto: Number(r.acconto) || 0, saldo: Number(r.saldo) || 0,
        acconto_method: r.acconto_method || "conto_corrente", saldo_method: r.saldo_method || "conto_corrente",
        payment_status: r.payment_status || "none", no_commission: !!r.no_commission,
        extra_guests: (r.extra_guests || []).map(({ _uid, ...g }) => g),
      });
      const primaryRoom = {
        ...(primaryId ? { id: primaryId } : {}),
        property_id: form.property_id, adults: Number(form.adults), children: Number(form.children),
        age_adults: Number(form.age_adults) || 0, age_child_5_13: Number(form.age_child_5_13) || 0,
        age_child_2_4: Number(form.age_child_2_4) || 0, age_infant: Number(form.age_infant) || 0,
        total_amount: Number(form.total_amount), acconto: Number(form.acconto), saldo: Number(form.saldo),
        acconto_method: form.acconto_method, saldo_method: form.saldo_method,
        payment_status: form.payment_status, no_commission: !!form.no_commission, commission: Number(form.commission) || 0,
        extra_guests: (form.extra_guests || []).map(({ _uid, ...g }) => g),
      };
      const groupPayload = {
        guest_name: form.guest_name, guest_surname: form.guest_surname,
        checkin: form.checkin, checkout: form.checkout, checkin_time: form.checkin_time, checkout_time: form.checkout_time,
        flight: form.flight, status: form.status, option_expiry_days: Number(form.option_expiry_days) || 0,
        cancellation: form.cancellation, notes: form.notes, origine: form.origine,
        passport_number: form.passport_number, nationality: form.nationality, birth_date: form.birth_date,
        sex: form.sex, expiry_date: form.expiry_date, country: form.country, passport_image: form.passport_image,
        guest_email: form.guest_email, guest_phone: form.guest_phone,
        rooms: [primaryRoom, ...extraRooms.map(mkRoom)],
      };
      try {
        await api.put(`/bookings/group/${groupEdit.group_id}`, groupPayload);
        toast.success("Gruppo aggiornato");
        onSaved();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setSubmitting(false);
      }
      return;
    }
    if (!editId && extraRooms.length > 0) {
      const mkRoom = (r) => ({
        property_id: r.property_id, adults: Number(r.adults) || 1, children: Number(r.children) || 0,
        age_adults: Number(r.age_adults) || Number(r.adults) || 0, age_child_5_13: Number(r.age_child_5_13) || 0,
        age_child_2_4: Number(r.age_child_2_4) || 0, age_infant: Number(r.age_infant) || 0,
        total_amount: Number(r.total_amount) || 0, acconto: Number(r.acconto) || 0, saldo: Number(r.saldo) || 0,
        acconto_method: r.acconto_method || "conto_corrente", saldo_method: r.saldo_method || "conto_corrente",
        payment_status: r.payment_status || "none", no_commission: !!r.no_commission,
        extra_guests: (r.extra_guests || []).map(({ _uid, ...g }) => g),
      });
      const primaryRoom = {
        property_id: form.property_id, adults: Number(form.adults), children: Number(form.children),
        age_adults: Number(form.age_adults) || 0, age_child_5_13: Number(form.age_child_5_13) || 0,
        age_child_2_4: Number(form.age_child_2_4) || 0, age_infant: Number(form.age_infant) || 0,
        total_amount: Number(form.total_amount), acconto: Number(form.acconto), saldo: Number(form.saldo),
        acconto_method: form.acconto_method, saldo_method: form.saldo_method,
        payment_status: form.payment_status, no_commission: !!form.no_commission, commission: Number(form.commission) || 0,
        extra_guests: (form.extra_guests || []).map(({ _uid, ...g }) => g),
      };
      const groupPayload = {
        guest_name: form.guest_name, guest_surname: form.guest_surname,
        checkin: form.checkin, checkout: form.checkout, checkin_time: form.checkin_time, checkout_time: form.checkout_time,
        flight: form.flight, status: form.status, option_expiry_days: Number(form.option_expiry_days) || 0,
        notes: form.notes, origine: form.origine,
        passport_number: form.passport_number, nationality: form.nationality, birth_date: form.birth_date,
        sex: form.sex, expiry_date: form.expiry_date, country: form.country, passport_image: form.passport_image,
        guest_email: form.guest_email, guest_phone: form.guest_phone,
        rooms: [primaryRoom, ...extraRooms.map(mkRoom)],
      };
      try {
        await api.post("/bookings/group", groupPayload);
        toast.success("Gruppo creato");
        onSaved();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setSubmitting(false);
      }
      return;
    }
    // Costruisce le tratte dello spostamento camera.
    // Se l'utente indica UNA sola tratta (la destinazione, es. "camera B dal 24 al 28"),
    // ricostruiamo automaticamente le tratte mancanti con la camera principale,
    // così lo spostamento risulta completo (es. A 20-24 + B 24-28).
    const buildSegments = (segs, mainRoom, ci, co) => {
      const valid = (segs || []).filter((s) => s.property_id && s.checkin && s.checkout);
      if (valid.length === 0) return [];
      if (valid.length >= 2) return valid;
      const s = valid[0];
      if (s.property_id === mainRoom && s.checkin === ci && s.checkout === co) return [];
      const out = [];
      if (s.checkin > ci) out.push({ property_id: mainRoom, checkin: ci, checkout: s.checkin });
      out.push(s);
      if (s.checkout < co) out.push({ property_id: mainRoom, checkin: s.checkout, checkout: co });
      return out.length >= 2 ? out : [];
    };
    const cleanSegs = buildSegments(form.segments, form.property_id, form.checkin, form.checkout);
    const payload = {
      ...form,
      adults: Number(form.adults), children: Number(form.children),
      age_adults: Number(form.age_adults) || 0, age_child_5_13: Number(form.age_child_5_13) || 0,
      age_child_2_4: Number(form.age_child_2_4) || 0, age_infant: Number(form.age_infant) || 0,
      segments: cleanSegs,
      total_amount: Number(form.total_amount), acconto: Number(form.acconto),
      saldo: Number(form.saldo), commission: Number(form.commission),
      option_expiry_days: Number(form.option_expiry_days) || 0,
      installments_enabled: !!form.installments_enabled,
      installments: form.installments_enabled
        ? (form.installments || []).map((r, i) => ({ amount: instAmount(i), due_date: r.due_date || "", method: r.method || "conto_corrente", paid: !!r.paid }))
        : [],
      extra_guests: (form.extra_guests || []).map(({ _uid, ...g }) => g),
    };
    try {
      if (editId) await api.put(`/bookings/${editId}`, payload);
      else await api.post("/bookings", payload);
      toast.success(editId ? "Prenotazione aggiornata" : "Prenotazione creata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const autoAges = () => {
    const ref = form.checkin;
    if (!ref) { toast.error("Imposta prima il check-in"); return; }
    const bds = [form.birth_date, ...(form.extra_guests || []).map((g) => g.birth_date)].filter(Boolean);
    let a = 0, c = 0, c2 = 0, inf = 0;
    bds.forEach((bd) => {
      const y = ageOn(bd, ref);
      if (y == null) return;
      if (y > 12) a++; else if (y >= 5) c++; else if (y > 2) c2++; else inf++;
    });
    setForm((f) => ({ ...f, age_adults: a, age_child_5_13: c, age_child_2_4: c2, age_infant: inf }));
    toast.success("Fasce d'età calcolate dalle date di nascita");
  };

  const currentOrigine = isAdmin ? form.origine : user?.agency_name;
  const manualNames = (agencies || []).filter((a) => a.manual).map((a) => a.name);
  const isDea = manualNames.includes(currentOrigine);

  const roomGroups = Object.entries(
    properties.reduce((acc, p) => {
      (acc[p.structure_name || "Camere"] = acc[p.structure_name || "Camere"] || []).push(p);
      return acc;
    }, {})
  );

  const selProp = properties.find((p) => p.id === form.property_id);
  const commissionOnly = !!selProp?.commission_only;

  const maAgency = (agencies || []).find((a) => a.name === currentOrigine);
  const maRates = {
    adult: Number(maAgency?.rate_adult ?? maAgency?.commission ?? 100),
    c512: Number(maAgency?.rate_child_5_12 ?? 50),
    c24: Number(maAgency?.rate_child_2_4 ?? 0),
    c02: Number(maAgency?.rate_child_0_2 ?? 0),
  };
  const maRoomTotal = (a, c, c2, inf) => maRates.adult * a + maRates.c512 * c + maRates.c24 * c2 + maRates.c02 * inf;
  const maRooms = [];
  if (selProp && !selProp.commission_only && !form.no_commission) {
    const a = Number(form.age_adults) || 0, c = Number(form.age_child_5_13) || 0, c2 = Number(form.age_child_2_4) || 0, inf = Number(form.age_infant) || 0;
    maRooms.push({ name: selProp.name || "Camera principale", a, c, c2, inf, total: maRoomTotal(a, c, c2, inf) });
  }
  (form.group_rooms || []).forEach((r) => {
    const p = properties.find((pp) => pp.id === r.property_id);
    if (p && !p.commission_only && !r.no_commission) {
      const a = Number(r.age_adults) || Number(r.adults) || 0, c = Number(r.age_child_5_13) || 0, c2 = Number(r.age_child_2_4) || 0, inf = Number(r.age_infant) || 0;
      maRooms.push({ name: p.name || "Camera", a, c, c2, inf, total: maRoomTotal(a, c, c2, inf) });
    }
  });
  const maGrandTotal = maRooms.reduce((s, r) => s + r.total, 0);
  const groupHasMa = (form.group_rooms || []).some((r) => { const p = properties.find((pp) => pp.id === r.property_id); return p && !p.commission_only; });
  const showMaBox = !commissionOnly || groupHasMa;
  const isMultiRoom = (form.group_rooms || []).length > 0;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
      {conflict && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="conflict-alert">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Camera già occupata (prenotazione {conflict.status}) dal <b>{conflict.checkin}</b> al <b>{conflict.checkout}</b>{conflict.guest ? ` — ${conflict.guest}` : ""}. Scegli un'altra camera o altre date.</span>
        </div>
      )}
      {!commissionOnly && <PassportScanner onResult={onScan} />}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-md bg-slate-50 border border-slate-200 p-3">
        <Field label="Stato prenotazione">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="f-status">
            <option value="opzionata">Opzionata</option>
            <option value="confermata">Confermata</option>
            <option value="cancellata">Cancellata</option>
          </select>
        </Field>
        {form.status === "opzionata" && (<>
          <Field label="Scadenza opzione (giorni)">
            <input type="number" min="1" value={form.option_expiry_days} onChange={(e) => set("option_expiry_days", e.target.value)} className={inputCls} data-testid="f-option-days" />
          </Field>
          <Field label="Scadenza opzione (data)">
            <input type="date" value={(form.option_expiry_date || "").slice(0, 10)} onChange={(e) => set("option_expiry_date", e.target.value)} className={inputCls} data-testid="f-option-expiry-date" />
          </Field>
          <div className="sm:col-span-3 flex flex-wrap items-end gap-2 rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="renew-option-panel">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-slate-600 mb-1">Rinnova opzione di (giorni)</label>
              <input type="number" min="1" value={renewDays} onChange={(e) => setRenewDays(e.target.value)} className={inputCls} data-testid="f-renew-days" placeholder="Es. 7" />
            </div>
            <Btn type="button" variant="secondary" className="!py-2" data-testid="f-renew-btn" onClick={renewOption}>Rinnova (oggi + giorni)</Btn>
          </div>
        </>)}
        {form.status !== "opzionata" && (
          <Field label="Stato pagamento">
            <select value={form.payment_status} onChange={(e) => set("payment_status", e.target.value)} className={inputCls} data-testid="f-payment-status">
              <option value="none">Nessun importo ricevuto</option>
              <option value="partial">Parzialmente saldata (acconto)</option>
              <option value="full">Totale saldato</option>
            </select>
          </Field>
        )}
        {form.status === "cancellata" && (
          <Field label="Voucher cancellazione">
            <select value={form.cancellation?.reason || ">60"} onChange={(e) => set("cancellation", { reason: e.target.value })} className={inputCls} data-testid="f-cancel-reason">
              <option value=">60">Più di 60 giorni — 100%</option>
              <option value="force_majeure">Force Majeure — 100%</option>
              <option value="30-60">Da 30 a 60 giorni — 70%</option>
              <option value="<30">Meno di 30 giorni — 50%</option>
              <option value="noshow">No-Show — 0%</option>
            </select>
          </Field>
        )}
      </div>

      {commissionOnly && (
        <div className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2" data-testid="commission-note">
          Struttura a commissione: inserisci solo numero persone e importo incassato (nessun dato ospite, nessun acconto/saldo).
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nome ospite">
          <input data-testid="f-name" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Cognome ospite">
          <input data-testid="f-surname" required value={form.guest_surname} onChange={(e) => set("guest_surname", e.target.value)} className={inputCls} />
        </Field>
        {!commissionOnly && (<>
        <Field label="Numero passaporto">
          <input data-testid="f-passport" value={form.passport_number} onChange={(e) => set("passport_number", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Nazionalità">
          <input data-testid="f-nationality" value={form.nationality} onChange={(e) => set("nationality", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Email ospite">
          <input data-testid="f-email" type="email" value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} className={inputCls} placeholder="ospite@email.com" />
        </Field>
        <Field label="Telefono / WhatsApp">
          <input data-testid="f-phone" value={form.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} className={inputCls} placeholder="+39..." />
        </Field>
        <Field label="Data di nascita">
          <input data-testid="f-birth" type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Scadenza documento">
          <input data-testid="f-expiry" type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className={inputCls} />
        </Field>
        </>)}

        <Field label="Camera">
          <select data-testid="f-property" required value={form.property_id} onChange={(e) => set("property_id", e.target.value)} className={inputCls}>
            <option value="">Seleziona…</option>
            {roomGroups.map(([grp, rooms]) => (
              <optgroup key={grp} label={grp}>
                {rooms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label="Origine prenotazione">
          {isAdmin ? (
            <select data-testid="f-origine" value={form.origine} onChange={(e) => set("origine", e.target.value)} className={inputCls}>
              {origini.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input value={user?.agency_name || ""} disabled className={`${inputCls} bg-slate-100`} />
          )}
        </Field>
        <Field label="Check-in">
          <input data-testid="f-checkin" type="date" required value={form.checkin} onChange={(e) => onCheckinChange(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Check-out">
          <input data-testid="f-checkout" type="date" required value={form.checkout} onChange={(e) => set("checkout", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Speedboat arrivo">
          <select data-testid="f-checkin-time" value={form.checkin_time || ""} onChange={(e) => set("checkin_time", e.target.value)} className={inputCls}>
            <option value="">Seleziona…</option>
            {sbOptions(sbSlots(sb, form.checkin, "arrivi"), form.checkin_time).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Speedboat partenza">
          <select data-testid="f-checkout-time" value={form.checkout_time || ""} onChange={(e) => set("checkout_time", e.target.value)} className={inputCls}>
            <option value="">Seleziona…</option>
            {sbOptions(sbSlots(sb, form.checkout, "partenze"), form.checkout_time).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Volo">
          <input data-testid="f-flight" value={form.flight} onChange={(e) => set("flight", e.target.value)} className={inputCls} placeholder="Es. EK652" />
        </Field>
        <Field label="Adulti">
          <input data-testid="f-adults" type="number" min="0" value={form.adults} onChange={(e) => set("adults", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Bambini">
          <input data-testid="f-children" type="number" min="0" value={form.children} onChange={(e) => set("children", e.target.value)} className={inputCls} />
        </Field>
        {!commissionOnly && (
          <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">Ripartizione ospiti per età (calcolo costi Keyodhoo)</span>
              <button type="button" onClick={autoAges} data-testid="auto-ages" className="text-xs text-sky-700 underline hover:text-sky-900">Calcola da date di nascita</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Adulti (>12)">
                <input data-testid="f-age-adults" type="number" min="0" value={form.age_adults} onChange={(e) => set("age_adults", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Bambini 5–12">
                <input data-testid="f-age-child" type="number" min="0" value={form.age_child_5_13} onChange={(e) => set("age_child_5_13", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Bambini 2–4">
                <input data-testid="f-age-child2" type="number" min="0" value={form.age_child_2_4} onChange={(e) => set("age_child_2_4", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Neonati <2">
                <input data-testid="f-age-infant" type="number" min="0" value={form.age_infant} onChange={(e) => set("age_infant", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>
        )}
        {showMaBox && (
          <div className="sm:col-span-2 rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="ma-summary">
            <div className="text-xs font-semibold text-sky-800 mb-1">Versamento Maldive Autentiche{isMultiRoom ? " (tutte le camere)" : " (camera principale)"}</div>
            {maRooms.length === 0 ? (
              <div className="text-sm text-slate-600">Nessun versamento dovuto (commissione non dovuta).</div>
            ) : maRooms.length === 1 ? (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                  <span>Adulti {maRooms[0].a} × {eur(maRates.adult)}</span>
                  <span>5–12: {maRooms[0].c} × {eur(maRates.c512)}</span>
                  <span>2–4: {maRooms[0].c2} × {eur(maRates.c24)}</span>
                  <span>0–1: {maRooms[0].inf} × {eur(maRates.c02)}</span>
                </div>
                <div className="mt-1 font-bold text-sky-800 text-base" data-testid="ma-summary-total">Totale: {eur(maGrandTotal)}</div>
              </>
            ) : (
              <>
                <div className="space-y-0.5">
                  {maRooms.map((r, i) => (
                    <div key={i} className="flex justify-between text-xs text-slate-600">
                      <span>{r.name} <span className="text-slate-400">({r.a}A · {r.c}·{r.c2}·{r.inf})</span></span>
                      <span className="tabular font-medium">{eur(r.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-1 pt-1 border-t border-sky-200 flex justify-between font-bold text-sky-800 text-base">
                  <span>Totale gruppo</span><span data-testid="ma-summary-total">{eur(maGrandTotal)}</span>
                </div>
              </>
            )}
            <div className="text-[11px] text-slate-400 mt-1">Tariffe agenzia "{currentOrigine}", modificabili in pagina Agenzie.</div>
          </div>
        )}
        <div className="sm:col-span-2 flex items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 p-3">
          <input type="checkbox" id="no-comm-check" data-testid="f-no-commission" checked={!!form.no_commission} onChange={(e) => set("no_commission", e.target.checked)} className="h-4 w-4 mt-0.5 accent-sky-600" />
          <label htmlFor="no-comm-check" className="text-sm text-slate-700 cursor-pointer">
            <span className="font-semibold">Commissione non dovuta</span> — l'intero Totale va direttamente in Entrate, nessuna politica di commissione applicata.
          </label>
        </div>
        <Field label="Totale (€)">
          <input data-testid="f-total" type="number" step="0.01" value={form.total_amount} onChange={(e) => onTotalChange(e.target.value)} className={inputCls} />
        </Field>
        {!commissionOnly && !form.installments_enabled && (<>
        <Field label="Metodo acconto">
          <select data-testid="f-acconto-method" value={form.acconto_method} onChange={(e) => set("acconto_method", e.target.value)} className={inputCls}>
            <option value="conto_corrente">Conto corrente</option>
            <option value="cash">Cash</option>
          </select>
        </Field>
        <Field label="Metodo saldo">
          <select data-testid="f-saldo-method" value={form.saldo_method} onChange={(e) => set("saldo_method", e.target.value)} className={inputCls}>
            <option value="conto_corrente">Conto corrente</option>
            <option value="cash">Cash</option>
          </select>
        </Field>
        <Field label="Acconto (€) — 30%">
          <input data-testid="f-acconto" type="number" step="0.01" value={form.acconto} onChange={(e) => set("acconto", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Scadenza acconto (7 gg da prenotazione)">
          <input data-testid="f-acconto-due" type="date" value={form.acconto_due_date || ""} onChange={(e) => set("acconto_due_date", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Saldo (€) — 70%">
          <input data-testid="f-saldo" type="number" step="0.01" value={form.saldo} onChange={(e) => set("saldo", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Scadenza saldo (21 gg prima arrivo)">
          <input data-testid="f-saldo-due" type="date" value={form.saldo_due_date || ""} onChange={(e) => set("saldo_due_date", e.target.value)} className={inputCls} />
        </Field>
        </>)}
        {!commissionOnly && !isGroupEdit && (form.group_rooms || []).length === 0 && !selProp?.esterna && (
          <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="installments-section">
            <div className="flex items-start gap-2.5">
              <input type="checkbox" id="inst-check" data-testid="f-installments-enabled" checked={!!form.installments_enabled} onChange={(e) => toggleInstallments(e.target.checked)} className="h-4 w-4 mt-0.5 accent-sky-600" />
              <label htmlFor="inst-check" className="text-sm text-slate-700 cursor-pointer">
                <span className="font-semibold">Rateizzazione</span> — acconto libero (inserito a mano) + 3 rate uguali (33% ciascuna) della rimanenza, al posto di acconto/saldo. Inserisci la scadenza di ogni rata.
              </label>
            </div>
            {form.installments_enabled && (
              <div className="mt-3 space-y-2" data-testid="installments-rows">
                {[0, 1, 2, 3].map((i) => {
                  const r = (form.installments || [])[i] || {};
                  return (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-[64px_1fr_1fr_1fr_auto] gap-2 items-end">
                      <div className="text-sm font-semibold text-slate-600 pb-2">{i === 0 ? "Acconto" : `Rata ${i}`}</div>
                      <Field label={i === 0 ? "Importo (€) manuale" : "Importo (€) · 1/3 rimanenza"}>
                        {i === 0
                          ? <input type="number" step="0.01" min="0" value={r.amount ?? ""} onChange={(e) => setInstallment(0, "amount", e.target.value)} placeholder="0,00" className={inputCls} data-testid="inst-amount-0" />
                          : <input readOnly value={instAmount(i).toFixed(2)} className={`${inputCls} bg-slate-100`} data-testid={`inst-amount-${i}`} />}
                      </Field>
                      <Field label="Scadenza">
                        <input type="date" value={(r.due_date || "").slice(0, 10)} onChange={(e) => setInstallment(i, "due_date", e.target.value)} className={inputCls} data-testid={`inst-due-${i}`} />
                      </Field>
                      <Field label="Metodo">
                        <select value={r.method || "conto_corrente"} onChange={(e) => setInstallment(i, "method", e.target.value)} className={inputCls} data-testid={`inst-method-${i}`}>
                          <option value="conto_corrente">Conto corrente</option>
                          <option value="cash">Cash</option>
                        </select>
                      </Field>
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 pb-2 whitespace-nowrap">
                        <input type="checkbox" checked={!!r.paid} onChange={(e) => setInstallment(i, "paid", e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid={`inst-paid-${i}`} />
                        Incassata
                      </label>
                    </div>
                  );
                })}
                <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="installments-summary">
                  <span>Totale prenotazione: <b>{eur(Number(form.total_amount || 0))}</b></span>
                  <span>Acconto: <b>{eur(instAcconto)}</b></span>
                  <span>Rimanenza in 3 rate: <b data-testid="inst-remainder">{eur(instRemainder)}</b></span>
                  <span>Incassato: <b className="text-emerald-700" data-testid="inst-paid-total">{eur(instPaid)}</b></span>
                  <span>Ancora da incassare: <b className="text-amber-700" data-testid="inst-to-collect">{eur(Math.max(Number(form.total_amount || 0) - instPaid, 0))}</b></span>
                </div>
                {!instAcconto && <div className="text-xs font-semibold text-rose-600" data-testid="inst-warning">Inserisci l'importo dell'acconto: senza acconto il totale viene diviso solo nelle 3 rate.</div>}
                {instAcconto > Number(form.total_amount || 0) && <div className="text-xs font-semibold text-rose-600" data-testid="inst-warning">L'acconto supera il totale della prenotazione di {eur(instAcconto - Number(form.total_amount || 0))}.</div>}
                <div className="text-[11px] text-slate-400">Non modifica contabilità/report.</div>
              </div>
            )}
          </div>
        )}
        {editId && isAdmin && <CardPayments bookingId={editId} phone={form.guest_phone} />}
        {isDea && (
          <Field label="Commissione dedicata (€)">
            <input data-testid="f-commission" type="number" step="0.01" value={form.commission} onChange={(e) => set("commission", e.target.value)} className={inputCls} />
          </Field>
        )}
        <div className="sm:col-span-2">
          <Field label="Note">
            <textarea data-testid="f-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>

      {!commissionOnly && !isGroupEdit && (
        <div className="rounded-md border border-slate-200 p-3" data-testid="segments-section">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-slate-700">Spostamenti camera (opzionale)</span>
            <button type="button" onClick={addSegment} data-testid="add-segment-btn" className="text-xs font-semibold text-sky-700 hover:text-sky-900 border border-sky-200 rounded-md px-2 py-1">+ Aggiungi tratta</button>
          </div>
          <p className="text-xs text-slate-400 mb-2">Aggiungi le tratte se l'ospite cambia camera durante il soggiorno. Puoi indicare anche solo la camera di destinazione (es. "camera B dal 24 al 28"): la tratta iniziale nella camera principale viene aggiunta in automatico. Lascia vuoto se resta nella stessa camera.</p>
          {(form.segments || []).map((s, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_36px] gap-2 items-end mb-2">
              <select value={s.property_id || ""} onChange={(e) => setSegment(i, "property_id", e.target.value)} className={inputCls} data-testid={`seg-room-${i}`}>
                <option value="">Camera…</option>
                {roomGroups.map(([grp, rooms]) => (
                  <optgroup key={grp} label={grp}>
                    {rooms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <input type="date" value={s.checkin || ""} onChange={(e) => setSegment(i, "checkin", e.target.value)} className={inputCls} data-testid={`seg-in-${i}`} />
              <input type="date" value={s.checkout || ""} onChange={(e) => setSegment(i, "checkout", e.target.value)} className={inputCls} data-testid={`seg-out-${i}`} />
              <button type="button" onClick={() => delSegment(i)} data-testid={`seg-del-${i}`} className="p-2 text-slate-400 hover:text-red-600"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {!commissionOnly && (!editId || isGroupEdit) && (
        <GroupRooms rooms={form.group_rooms || []} onChange={(g) => set("group_rooms", g)} roomGroups={roomGroups} />
      )}

      {!commissionOnly && <ExtraGuests guests={form.extra_guests || []} onChange={(g) => set("extra_guests", g)} />}

      <div className="flex justify-end gap-2 pt-2">
        <Btn type="button" variant="secondary" onClick={onCancel}>Annulla</Btn>
        <Btn type="submit" data-testid="save-booking" disabled={submitting || !!conflict}>{submitting ? "Salvataggio…" : (editId || isGroupEdit) ? "Salva" : "Crea"}</Btn>
      </div>
    </form>
  );
}

